import config from '@/config';

import { useCallback, useRef, useState } from 'react';

import { useParams } from 'react-router';

import {
  useDeployTransformerMutation,
  useExecuteTransformerMutation,
  usePostTransformerEventDataMutation,
  useSaveTriggerMutation,
} from '@/features/transformers/api/transformersApi';
import { logger } from '@/utils/logger';

import { useSaveAsNewVersionMutation, useSaveRecipeContentMutation } from '../api/recipeEditorApi';
import { useRecipeEditor } from '../context/RecipeEditorContext';
import { emitRecipeSaved, setRecipeLoading } from '../events/recipeEditorEvents';
import { recipeToYaml } from '../utils/recipeConverter';

interface UseToolbarActionsOptions {
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Promise return type is dynamic
  refetch?: () => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recipe data is dynamic
  setRecipe?: (recipe: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ReactFlow nodes array
  setNodes?: (nodes: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ReactFlow edges array
  setEdges?: (edges: any[]) => void;
  setYamlContent?: (content: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recipe converter function types are dynamic
  recipeToNodes?: (recipe: any) => { nodes: any[]; edges: any[] };
  // Execution context - needed to match legacy dataProtect behavior
  environments?: Array<{ id: string; name: string }>;
  tenantKey?: string;
  appUniqueKey?: string;
}

/**
 * Hook that provides all toolbar action handlers
 * Integrates with RecipeEditorContext, Monaco editor, and RTK Query APIs
 */
export const useToolbarActions = (options: UseToolbarActionsOptions = {}) => {
  const {
    onError,
    onSuccess,
    refetch,
    setRecipe,
    setNodes,
    setEdges,
    setYamlContent,
    recipeToNodes,
    environments = [],
    tenantKey = '',
    appUniqueKey = '',
  } = options;
  const { state, dispatch, setIsModified } = useRecipeEditor();
  const { appId, recipeId } = useParams<{ appId: string; recipeId: string }>();

  // Monaco editor ref (should be set by YamlEditor component)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Monaco editor type
  const editorRef = useRef<any>(null);

  // Error state for components that don't pass callbacks
  const [lastError, setLastError] = useState<string | null>(null);

  // Menu anchor states
  const [saveMenuAnchor, setSaveMenuAnchor] = useState<HTMLElement | null>(null);
  const [runMenuAnchor, setRunMenuAnchor] = useState<HTMLElement | null>(null);
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState<HTMLElement | null>(null);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<HTMLElement | null>(null);
  const [editMenuAnchor, setEditMenuAnchor] = useState<HTMLElement | null>(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState<HTMLElement | null>(null);

  // Helper to report errors
  const reportError = useCallback(
    (message: string) => {
      logger.error('[Toolbar Error]', message);
      setLastError(message);
      onError?.(message);
    },
    [onError]
  );

  // Helper to report success
  const reportSuccess = useCallback(
    (message: string) => {
      onSuccess?.(message);
    },
    [onSuccess]
  );

  // RTK Query mutations
  const [saveRecipe] = useSaveRecipeContentMutation();
  const [_saveAsNewVersion] = useSaveAsNewVersionMutation();
  const [executeTransformer] = useExecuteTransformerMutation();
  const [postEventContext] = usePostTransformerEventDataMutation();
  const [_deployTransformer] = useDeployTransformerMutation();
  const [_saveTrigger] = useSaveTriggerMutation();

  // ========================================
  // FILE OPERATIONS
  // ========================================

  const handleUndo = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.trigger('keyboard', 'undo', null);
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.trigger('keyboard', 'redo', null);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!recipeId || !appId) {
      reportError('No recipe selected');
      return;
    }

    // Block saves for deployed or old (non-latest) versions (matches legacy dataProtect)
    if (state.isReadOnly) {
      return;
    }

    try {
      dispatch({ type: 'SET_IS_SAVING', payload: true });

      // Include all metadata required by the API
      const metadata = state.recipeMetadata;

      // Build request based on editor mode
      const saveRequest = {
        baseId: recipeId,
        appId,
        editorMode: state.editorMode,
        saveMode: 'sameVersion' as const,
        // Content based on mode
        ...(state.editorMode === 'visual'
          ? {
              // Visual mode: Use UPDATED_RECIPE from sessionStorage (has latest changes from Flow)
              // The recipe in state is stale because Flow doesn't sync back its local nodes/edges
              config: (() => {
                const updatedRecipe = JSON.parse(sessionStorage.getItem('UPDATED_RECIPE') || '{}');
                return recipeToYaml(
                  Object.keys(updatedRecipe).length > 0 ? updatedRecipe : state.recipe
                );
              })(),
              configJson: JSON.stringify({
                recipe: JSON.parse(
                  sessionStorage.getItem('UPDATED_RECIPE') || JSON.stringify(state.recipe || {})
                ),
              }),
            }
          : {
              // YAML mode: send ONLY yamlContent (no ConfigJson)
              yamlContent: state.yamlContent,
            }),
        // Metadata fields
        versionId: metadata?.versionId,
        name: metadata?.name,
        environ: metadata?.environ,
        version: state.currentVersion,
        description: metadata?.description,
        languageVersion: metadata?.languageVersion,
        processingType: metadata?.processingType,
        maxDataProcTime: metadata?.maxDataProcTime,
        templateName: metadata?.templateName,
      };

      const response = await saveRecipe(saveRequest).unwrap();

      // Update local state with server response
      if (state.editorMode === 'yaml' && response.config && setYamlContent) {
        // YAML mode: update YAML content from server
        setYamlContent(response.config);
      } else if (state.editorMode === 'visual') {
        // Visual mode: update recipe from server response or from what we saved
        try {
          let updatedRecipe;
          if (response.ConfigJson) {
            // Use server's response if available
            const parsed = JSON.parse(response.ConfigJson);
            updatedRecipe = parsed.recipe || parsed;
          } else {
            // Fallback to what we just saved
            updatedRecipe = JSON.parse(
              sessionStorage.getItem('UPDATED_RECIPE') || JSON.stringify(state.recipe || {})
            );
          }

          if (setRecipe) {
            setRecipe(updatedRecipe);
          }

          // CRITICAL: Update sessionStorage to reflect the saved state
          // This ensures future comparisons are against the saved version
          sessionStorage.setItem('CURRENT_RECIPE_FROM_NETWORK', JSON.stringify(updatedRecipe));
          // Clear UPDATED_RECIPE since we're now in sync with the network
          sessionStorage.removeItem('UPDATED_RECIPE');
        } catch (error) {
          logger.error('[handleSave] Failed to parse ConfigJson from response:', error);
        }
      }

      // Also update metadata with latest config
      if (response.config || response.id) {
        dispatch({
          type: 'SET_RECIPE_METADATA',
          payload: {
            versionId: response.id ? String(response.id) : state.recipeMetadata?.versionId || '',
            name: state.recipeMetadata?.name || '',
            config: response.config || state.recipeMetadata?.config || '',
            environ: state.recipeMetadata?.environ || '',
            description: state.recipeMetadata?.description || '',
            languageVersion: state.recipeMetadata?.languageVersion || '',
            processingType: state.recipeMetadata?.processingType || '',
            maxDataProcTime: state.recipeMetadata?.maxDataProcTime || '',
            templateName: state.recipeMetadata?.templateName || '',
          },
        });
      }

      setIsModified(false);

      // Emit event: recipe saved
      emitRecipeSaved();

      reportSuccess('Recipe saved successfully');
    } catch (error) {
      logger.error('[handleSave] Save error:', error);
      const message = error instanceof Error ? error.message : 'Failed to save recipe';
      reportError(message);
    } finally {
      dispatch({ type: 'SET_IS_SAVING', payload: false });
    }
  }, [
    recipeId,
    appId,
    state.isReadOnly,
    state.editorMode,
    state.recipe,
    state.yamlContent,
    state.recipeMetadata,
    state.currentVersion,
    saveRecipe,
    dispatch,
    setIsModified,
    setYamlContent,
    setRecipe,
    reportError,
    reportSuccess,
  ]);

  const toggleAutosave = useCallback(() => {
    // Don't allow enabling autosave when read-only (deployed or old version)
    if (state.isReadOnly) return;
    dispatch({ type: 'SET_AUTOSAVE_ENABLED', payload: !state.autosaveEnabled });
  }, [dispatch, state.autosaveEnabled, state.isReadOnly]);

  const handleDiscardChanges = useCallback(async () => {
    if (!state.isModified) {
      reportError('No changes to discard');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to discard all changes? This will reload the last saved version.'
    );

    if (!confirmed) return;

    try {
      if (!refetch) {
        reportError('Unable to reload recipe');
        return;
      }

      // Refetch from server
      const freshData = await refetch();

      if (freshData.data && setRecipe && setNodes && setEdges && setYamlContent && recipeToNodes) {
        const recipeContent = freshData.data;

        // CRITICAL: Set loading flag BEFORE updating nodes/edges to prevent false modification events
        setRecipeLoading(true);

        // Update recipe, nodes, edges, and YAML
        setRecipe(recipeContent.recipe);
        const { nodes: newNodes, edges: newEdges } = recipeToNodes(recipeContent.recipe);
        setNodes(newNodes);
        setEdges(newEdges);
        setYamlContent(recipeContent.config || '');

        // Update sessionStorage
        sessionStorage.setItem('CURRENT_RECIPE_FROM_NETWORK', JSON.stringify(recipeContent.recipe));
        sessionStorage.removeItem('UPDATED_RECIPE');

        // Force ReactFlow remount
        dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });

        // Reset modified state
        dispatch({ type: 'SET_IS_MODIFIED', payload: false });

        // Clear loading flag after all state updates
        setTimeout(() => {
          setRecipeLoading(false);
        }, 0);

        reportSuccess('Changes discarded successfully');
      }
    } catch (error) {
      logger.error('[Discard Changes] Error:', error);
      reportError('Failed to discard changes');
    }
  }, [
    state.isModified,
    refetch,
    setRecipe,
    setNodes,
    setEdges,
    setYamlContent,
    recipeToNodes,
    dispatch,
    reportError,
    reportSuccess,
  ]);

  // ========================================
  // EXECUTION
  // ========================================

  /**
   * Get the current version's ID (not the base/recipe ID).
   * In dataProtect, thisTransformer() finds the version object matching curVersion,
   * and thisTrans.Id is the VERSION-specific ID used for execute calls.
   * The base ID (recipeId from URL params) is only used for eventdata POST.
   */
  const getCurrentVersionId = useCallback((): string => {
    if (state.recipeMetadata?.versionId) {
      return state.recipeMetadata.versionId;
    }
    const currentVer = state.versions.find(
      (v) => parseInt(String(v.version), 10) === state.currentVersion
    );
    if (currentVer?.id) {
      return currentVer.id;
    }
    throw new Error(
      'Cannot execute: recipe version ID is not available. ' +
        'Please reload the recipe and try again.'
    );
  }, [state.recipeMetadata, state.versions, state.currentVersion]);

  /**
   * Generate execution query parameters and event data.
   *
   * Matches legacy dataProtect behavior (edittrans.js):
   * 1. Build event data object with environment info
   * 2. Build query string with action/path/at/type/externalExecutionId/triggeredBy
   * 3. Environment is NOT sent as a query param - it's sent via POST to eventdata endpoint
   * 4. Uses VERSION ID (thisTrans.Id) for execute URL and externalExecutionId, NOT base ID
   */
  const createExecutionQuery = useCallback(
    (mode: 'runAsJob' | 'runAsEvent') => {
      const timestamp = Date.now();
      // Legacy uses VERSION ID for externalExecutionId: "" + thisTrans.Id + milliSeconds
      const versionId = getCurrentVersionId();
      const externalExecutionId = `${versionId}${timestamp}`;

      // Get user info for triggeredBy
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const triggeredBy = profile?.name || profile?.email || 'unknown';

      // Build event data object (matches legacy startRecipeDirect in edittrans.js)
      const ftpDir = config.FTP_DIR || 'organizations/';
      const fullPath = `/${ftpDir}${tenantKey}/${appUniqueKey}/sample.zip`;
      const eventDate = new Date().toISOString();

      const eventData: Record<string, string> = {
        action: 'create',
        fullPath,
        eventDate,
        type: 'file',
      };

      // For runAsJob, add environment info to event data
      if (mode === 'runAsJob' && state.selectedEnvironment) {
        const selectedEnv = environments.find((env) => env.id === state.selectedEnvironment);
        if (selectedEnv) {
          eventData.environmentName = selectedEnv.name;
          eventData.environmentId = String(selectedEnv.id);
        }
      }

      // Include templateName if selected (legacy edittrans.js:2027-2029)
      if (state.selectedTemplate) {
        eventData.templateName = state.selectedTemplate;
      }

      // Build query string matching legacy createNewQuery (edittrans.js:2159-2181)
      // NOTE: Environment is NOT a query param - backend reads it from posted event context
      const params = new URLSearchParams();
      params.append('action', eventData.action);
      params.append('path', eventData.fullPath);
      params.append('at', eventData.eventDate);
      params.append('type', eventData.type);
      params.append('externalExecutionId', externalExecutionId);
      params.append('triggeredBy', triggeredBy);

      return { queryParams: params.toString(), externalExecutionId, eventData, versionId };
    },
    [
      getCurrentVersionId,
      state.selectedEnvironment,
      state.selectedTemplate,
      environments,
      tenantKey,
      appUniqueKey,
    ]
  );

  /**
   * Execute recipe immediately (Run as Job) or open Event Context editor (Run as Event).
   * Matches legacy edittrans.js: startRecipeDirect for runAsJob, handleRun for runAsEvent.
   */
  const handleRun = useCallback(
    async (mode: 'runAsJob' | 'runAsEvent') => {
      if (!recipeId) {
        reportError('No recipe selected');
        return;
      }

      // ---- Run as Event: open Event Context tab, don't execute yet ----
      if (mode === 'runAsEvent') {
        if (!state.selectedEnvironment) {
          reportError('Please select an environment');
          return;
        }

        // Toggle: if Event Context tab exists, remove it (matches legacy handleRun)
        const existingTab = state.bottomPanelTabs.find((t) => t.type === 'eventContext');
        if (existingTab) {
          dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: existingTab.id });
          return;
        }

        // Add Event Context tab
        dispatch({
          type: 'ADD_BOTTOM_TAB',
          payload: {
            id: `event-context-${Date.now()}`,
            title: 'Event Context',
            type: 'eventContext',
            closeable: true,
          },
        });
        return;
      }

      // ---- Run as Job: execute immediately ----
      if (!state.selectedEnvironment) {
        reportError('Please select an environment to run as job');
        return;
      }

      // Remove any existing "Recipe Running..." or "Results" tab
      const existingRunTab = state.bottomPanelTabs.find((t) => t.type === 'runningStatus');
      if (existingRunTab) {
        dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: existingRunTab.id });
      }

      // Track the running tab so we can clean it up if execution fails to start
      let runningTabId: string | null = null;
      try {
        dispatch({ type: 'SET_IS_EXECUTING', payload: true });

        // Step 0: Save current recipe content before executing
        // (matches legacy edittrans.js: startRecipeDirect calls handleSubmit() first)
        if (state.isModified && !state.isReadOnly) {
          await handleSave();
        }

        const { queryParams, externalExecutionId, eventData, versionId } =
          createExecutionQuery(mode);

        // Store the externalExecutionId for RunningStatusPanel polling
        dispatch({
          type: 'SET_CURRENT_EXTERNAL_EXECUTION_ID',
          payload: externalExecutionId,
        });

        // Add "Recipe Running..." tab (not closeable while running)
        runningTabId = `running-${externalExecutionId}`;
        dispatch({
          type: 'ADD_BOTTOM_TAB',
          payload: {
            id: runningTabId,
            title: 'Recipe Running...',
            type: 'runningStatus',
            closeable: false,
          },
        });

        // Step 1: POST event context with environment info BEFORE executing
        // Uses BASE ID (recipeId) — matches legacy: callPostEventContext uses match.params.transId
        await postEventContext({
          transBaseId: recipeId,
          data: { eventData },
        }).unwrap();

        // Step 2: Execute the recipe
        // Uses VERSION ID (versionId) — matches legacy: callExecuteTransformer uses thisTrans.Id
        await executeTransformer({
          transId: versionId,
          queryParams,
        }).unwrap();

        reportSuccess('Recipe execution started');

        // NOTE: isExecuting intentionally stays `true` here. The execute call only
        // *initiates* the run and returns immediately; the recipe keeps running and
        // is polled by RunningStatusPanel. The Run button must remain disabled until
        // the execution actually completes (RunningStatusPanel resets isExecuting),
        // is stopped, or fails — otherwise rapid clicks trigger duplicate executions.
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to execute recipe';
        reportError(message);
        // Execution failed to start: re-enable the Run button and clean up the running tab
        dispatch({ type: 'SET_IS_EXECUTING', payload: false });
        if (runningTabId) {
          dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: runningTabId });
        }
        dispatch({ type: 'SET_CURRENT_EXTERNAL_EXECUTION_ID', payload: null });
      }
    },
    [
      recipeId,
      state.selectedEnvironment,
      state.isModified,
      state.isReadOnly,
      state.bottomPanelTabs,
      handleSave,
      postEventContext,
      executeTransformer,
      dispatch,
      createExecutionQuery,
      reportError,
      reportSuccess,
    ]
  );

  /**
   * Execute recipe from Event Context panel (after user edits event JSON and clicks Start Recipe).
   * This matches the legacy flow: Event Context "Start Recipe" button → startRecipe().
   */
  const executeFromEventContext = useCallback(
    async (eventData: Record<string, string>) => {
      if (!recipeId) {
        reportError('No recipe selected');
        return;
      }

      // Remove Event Context tab
      const eventTab = state.bottomPanelTabs.find((t) => t.type === 'eventContext');
      if (eventTab) {
        dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: eventTab.id });
      }

      // Remove any existing running tab
      const existingRunTab = state.bottomPanelTabs.find((t) => t.type === 'runningStatus');
      if (existingRunTab) {
        dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: existingRunTab.id });
      }

      // Track the running tab so we can clean it up if execution fails to start
      let runningTabId: string | null = null;
      try {
        dispatch({ type: 'SET_IS_EXECUTING', payload: true });

        // Save before executing (matches legacy edittrans.js: handleSubmit before startRecipe)
        if (state.isModified && !state.isReadOnly) {
          await handleSave();
        }

        const timestamp = Date.now();
        // Use VERSION ID for externalExecutionId (matches legacy: "" + thisTrans.Id + milliSeconds)
        const versionId = getCurrentVersionId();
        const externalExecutionId = `${versionId}${timestamp}`;

        const profile = JSON.parse(localStorage.getItem('profile') || '{}');
        const triggeredBy = profile?.name || profile?.email || 'unknown';

        // Store the externalExecutionId
        dispatch({
          type: 'SET_CURRENT_EXTERNAL_EXECUTION_ID',
          payload: externalExecutionId,
        });

        // Add "Recipe Running..." tab
        runningTabId = `running-${externalExecutionId}`;
        dispatch({
          type: 'ADD_BOTTOM_TAB',
          payload: {
            id: runningTabId,
            title: 'Recipe Running...',
            type: 'runningStatus',
            closeable: false,
          },
        });

        // Build query from event data (matches legacy createNewQuery)
        const params = new URLSearchParams();
        params.append('action', eventData.action || 'create');
        params.append('path', eventData.fullPath || '');
        params.append('at', eventData.eventDate || new Date().toISOString());
        params.append('type', eventData.type || 'file');
        params.append('externalExecutionId', externalExecutionId);
        params.append('triggeredBy', triggeredBy);

        // Post event context — uses BASE ID (matches legacy: callPostEventContext uses match.params.transId)
        await postEventContext({
          transBaseId: recipeId,
          data: { eventData },
        }).unwrap();

        // Execute — uses VERSION ID (matches legacy: callExecuteTransformer uses thisTrans.Id)
        await executeTransformer({
          transId: versionId,
          queryParams: params.toString(),
        }).unwrap();

        reportSuccess('Recipe execution started');

        // NOTE: isExecuting intentionally stays `true` here — see handleRun for rationale.
        // RunningStatusPanel resets it once the execution completes or is stopped.
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to execute recipe';
        reportError(message);
        // Execution failed to start: re-enable the Run button and clean up the running tab
        dispatch({ type: 'SET_IS_EXECUTING', payload: false });
        if (runningTabId) {
          dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: runningTabId });
        }
        dispatch({ type: 'SET_CURRENT_EXTERNAL_EXECUTION_ID', payload: null });
      }
    },
    [
      recipeId,
      state.isModified,
      state.isReadOnly,
      state.bottomPanelTabs,
      handleSave,
      getCurrentVersionId,
      postEventContext,
      executeTransformer,
      dispatch,
      reportError,
      reportSuccess,
    ]
  );

  const swapJobType = useCallback(() => {
    dispatch({
      type: 'SET_JOB_MODE',
      payload: state.jobMode === 'runAsJob' ? 'runAsEvent' : 'runAsJob',
    });
  }, [dispatch, state.jobMode]);

  // ========================================
  // DEPLOYMENT
  // ========================================

  const handleDeploy = useCallback(async () => {
    if (!appId || !recipeId || !state.selectedEnvironment) return;

    try {
      dispatch({ type: 'SET_IS_DEPLOYING', payload: true });
      dispatch({ type: 'SET_SHOW_DEPLOY_DIALOG', payload: true });

      // Deploy dialog will handle the actual deployment
    } catch (error) {
      logger.error('Failed to deploy:', error);
    } finally {
      dispatch({ type: 'SET_IS_DEPLOYING', payload: false });
    }
  }, [appId, recipeId, state.selectedEnvironment, dispatch]);

  // ========================================
  // PANELS/VIEWS
  // ========================================

  const toggleExecutionLog = useCallback(() => {
    const existing = state.bottomPanelTabs.find((t) => t.type === 'executionLog');
    if (existing) {
      dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: existing.id });
    } else {
      dispatch({
        type: 'ADD_BOTTOM_TAB',
        payload: {
          id: `exec-log-${Date.now()}`,
          title: 'Execution Log',
          type: 'executionLog',
          closeable: true,
        },
      });
    }
  }, [dispatch, state.bottomPanelTabs]);

  const toggleCompare = useCallback(() => {
    dispatch({ type: 'TOGGLE_COMPARE' });
  }, [dispatch]);

  const toggleMetadata = useCallback(() => {
    dispatch({ type: 'TOGGLE_METADATA' });
    if (!state.showMetadata) {
      dispatch({ type: 'SET_SHOW_METADATA_DIALOG', payload: true });
    }
  }, [dispatch, state.showMetadata]);

  // ========================================
  // EDITOR UTILITIES (YAML mode)
  // ========================================

  const toggleComment = useCallback(() => {
    if (editorRef.current) {
      const action = editorRef.current.getAction('editor.action.commentLine');
      action?.run();
    }
  }, []);

  const formatDocument = useCallback(() => {
    if (editorRef.current) {
      const action = editorRef.current.getAction('editor.action.formatDocument');
      action?.run();
    }
  }, []);

  const toggleSchemaFold = useCallback(() => {
    if (!editorRef.current) return;

    try {
      const foldingController = editorRef.current.getContribution('editor.contrib.folding');
      const model = editorRef.current.getModel();

      if (!model) return;

      if (state.schemasFolded) {
        // Unfold all
        const action = editorRef.current.getAction('editor.unfoldAll');
        action?.run();
      } else {
        // Find and fold schema sections
        const schemaMatches = model.findMatches('schema:', true, false, true, null, true);
        const schemasMatches = model.findMatches('schemas:', true, false, true, null, true);

        const allMatches = [...schemaMatches, ...schemasMatches];

        if (allMatches.length > 0 && foldingController) {
          // Fold regions containing schemas
          allMatches.forEach((match) => {
            const lineNumber = match.range.startLineNumber;
            editorRef.current?.setPosition({ lineNumber, column: 1 });
            const action = editorRef.current?.getAction('editor.fold');
            action?.run();
          });
        }
      }

      dispatch({ type: 'SET_SCHEMAS_FOLDED', payload: !state.schemasFolded });
    } catch (error) {
      logger.error('Failed to toggle schema fold:', error);
    }
  }, [dispatch, state.schemasFolded]);

  const toggleMinimap = useCallback(() => {
    dispatch({ type: 'TOGGLE_MINIMAP' });

    if (editorRef.current) {
      editorRef.current.updateOptions({
        minimap: { enabled: !state.showMinimap },
      });
    }
  }, [dispatch, state.showMinimap]);

  const openSqlEditor = useCallback(() => {
    dispatch({ type: 'SET_SHOW_SQL_EDITOR_DIALOG', payload: true });
  }, [dispatch]);

  // ========================================
  // EDITOR MODE
  // ========================================

  const handleEditorModeChange = useCallback(
    async (mode: 'visual' | 'yaml') => {
      try {
        // Set switching state immediately
        dispatch({ type: 'SET_IS_SWITCHING_MODE', payload: true });

        // If switching from YAML to visual with unsaved changes, parse the YAML
        if (state.editorMode === 'yaml' && mode === 'visual' && state.isModified) {
          const confirmed = window.confirm(
            'You have unsaved YAML changes. Do you want to view them in visual mode? (Click OK to view, Cancel to discard and reload from server)'
          );

          if (confirmed) {
            // Parse YAML and convert to visual representation
            try {
              // Parse YAML to recipe
              const yamlParser = await import('js-yaml');
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- YAML parsed object is dynamic
              const parsed = yamlParser.load(state.yamlContent) as any;
              const recipe = parsed.recipe || parsed;

              // CRITICAL: Set loading flag BEFORE updating nodes/edges to prevent false modification events
              setRecipeLoading(true);

              // Update recipe in context
              if (setRecipe) {
                setRecipe(recipe);
              }

              // Convert to nodes/edges
              if (recipeToNodes && setNodes && setEdges) {
                const { nodes: newNodes, edges: newEdges } = recipeToNodes(recipe);
                setNodes(newNodes);
                setEdges(newEdges);
              }

              // Update sessionStorage so Flow picks up the changes
              sessionStorage.setItem('CURRENT_RECIPE_FROM_NETWORK', JSON.stringify(recipe));
              sessionStorage.setItem('UPDATED_RECIPE', JSON.stringify(recipe));

              // Close Log and Compare panels when switching to visual mode
              if (mode === 'visual') {
                dispatch({ type: 'SET_SHOW_COMPARE', payload: false });
              }

              // Force ReactFlow to remount with updated data
              dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });

              // Clear loading flag after all state updates (keep isModified true since user kept changes)
              setTimeout(() => {
                setRecipeLoading(false);
              }, 0);

              // Switch mode - keep isModified true
              dispatch({ type: 'SET_EDITOR_MODE', payload: mode });
              return;
            } catch (parseError) {
              logger.error('[Mode Switch] Failed to parse YAML:', parseError);
              reportError('Failed to parse YAML. Please fix syntax errors before switching modes.');
              return;
            } finally {
              // Clear switching state after a brief delay for smooth transition
              setTimeout(() => {
                dispatch({ type: 'SET_IS_SWITCHING_MODE', payload: false });
              }, 300);
            }
          }
          // If user clicked Cancel, fall through to refetch from server
        } else if (state.editorMode === 'visual' && mode === 'yaml' && state.isModified) {
          // Switching from visual to YAML with unsaved changes
          const confirmed = window.confirm(
            'You have unsaved changes. Switching to YAML mode will discard them. Continue?'
          );
          if (!confirmed) {
            dispatch({ type: 'SET_IS_SWITCHING_MODE', payload: false });
            return;
          }
          // Fall through to refetch from server
        }

        // For all other cases, or if user chose to discard changes, refetch from server
        if (!refetch) {
          logger.warn('[Mode Switch] refetch not available, switching without refresh');
          // Close Compare panel when switching to visual mode
          if (mode === 'visual') {
            dispatch({ type: 'SET_SHOW_COMPARE', payload: false });
          }
          dispatch({ type: 'SET_EDITOR_MODE', payload: mode });
          return;
        }

        const freshData = await refetch();

        if (freshData.data) {
          const recipeContent = freshData.data;

          // CRITICAL: Set loading flag BEFORE updating nodes/edges to prevent false modification events
          setRecipeLoading(true);

          // Update both YAML and Visual representations with server data
          if (recipeContent.recipe && setRecipe && setNodes && setEdges && recipeToNodes) {
            setRecipe(recipeContent.recipe);
            const { nodes: newNodes, edges: newEdges } = recipeToNodes(recipeContent.recipe);
            setNodes(newNodes);
            setEdges(newEdges);
          }

          if (setYamlContent) {
            setYamlContent(recipeContent.config || '');
          }

          // Update sessionStorage with fresh data
          if (recipeContent.recipe) {
            sessionStorage.setItem(
              'CURRENT_RECIPE_FROM_NETWORK',
              JSON.stringify(recipeContent.recipe)
            );
            sessionStorage.removeItem('UPDATED_RECIPE');
          }

          // Update metadata
          dispatch({
            type: 'SET_RECIPE_METADATA',
            payload: {
              versionId: recipeContent.versionId,
              name: recipeContent.name || '',
              config: recipeContent.config || '',
              environ: recipeContent.environ || '',
              description: recipeContent.description || '',
              languageVersion: recipeContent.languageVersion || '2.0',
              processingType: recipeContent.processingType || 'batch',
              maxDataProcTime: recipeContent.maxDataProcTime || '00:00:00',
              templateName: recipeContent.templateName || '',
            },
          });

          setIsModified(false);

          // Clear loading flag after all state updates
          setTimeout(() => {
            setRecipeLoading(false);
          }, 0);
        }

        // Close Compare panel when switching to visual mode
        if (mode === 'visual') {
          dispatch({ type: 'SET_SHOW_COMPARE', payload: false });
        }

        // Force ReactFlow to remount with fresh data after mode switch
        dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });

        // Now switch the mode
        dispatch({ type: 'SET_EDITOR_MODE', payload: mode });

        // Clear switching state after a brief delay for smooth transition
        setTimeout(() => {
          dispatch({ type: 'SET_IS_SWITCHING_MODE', payload: false });
        }, 300);
      } catch (error) {
        logger.error('[Mode Switch] Failed to switch modes:', error);
        reportError('Failed to switch modes. Please try again.');
        dispatch({ type: 'SET_IS_SWITCHING_MODE', payload: false });
      }
    },
    [
      state.isModified,
      state.editorMode,
      state.yamlContent,
      refetch,
      dispatch,
      setRecipe,
      setNodes,
      setEdges,
      setYamlContent,
      setIsModified,
      recipeToNodes,
      reportError,
    ]
  );

  // ========================================
  // VERSION MANAGEMENT
  // ========================================

  const openVersionsDialog = useCallback(() => {
    dispatch({ type: 'SET_SHOW_VERSIONS_DIALOG', payload: true });
  }, [dispatch]);

  const openCheckInDialog = useCallback(() => {
    dispatch({ type: 'SET_SHOW_CHECKIN_DIALOG', payload: true });
  }, [dispatch]);

  // ========================================
  // MODALS
  // ========================================

  const openScheduleDialog = useCallback(() => {
    dispatch({ type: 'SET_SHOW_SCHEDULE_DIALOG', payload: true });
  }, [dispatch]);

  const openCommandPalette = useCallback(() => {
    dispatch({ type: 'SET_SHOW_COMMAND_PALETTE', payload: true });
  }, [dispatch]);

  // ========================================
  // MENU HANDLERS
  // ========================================

  const handleSaveMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setSaveMenuAnchor(saveMenuAnchor ? null : event.currentTarget);
    },
    [saveMenuAnchor]
  );

  const handleRunMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setRunMenuAnchor(runMenuAnchor ? null : event.currentTarget);
    },
    [runMenuAnchor]
  );

  const handleToolsMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setToolsMenuAnchor(toolsMenuAnchor ? null : event.currentTarget);
    },
    [toolsMenuAnchor]
  );

  const handleTemplateMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setTemplateMenuAnchor(templateMenuAnchor ? null : event.currentTarget);
    },
    [templateMenuAnchor]
  );

  const openTemplateDialog = useCallback(() => {
    dispatch({ type: 'SET_SHOW_TEMPLATE_DIALOG', payload: true });
  }, [dispatch]);

  const handleEditMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setEditMenuAnchor(editMenuAnchor ? null : event.currentTarget);
    },
    [editMenuAnchor]
  );

  const handleViewMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setViewMenuAnchor(viewMenuAnchor ? null : event.currentTarget);
    },
    [viewMenuAnchor]
  );

  const handleEnvironmentChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI Select event type
    (event: any) => {
      dispatch({ type: 'SET_SELECTED_ENVIRONMENT', payload: event.target.value });
    },
    [dispatch]
  );

  // ========================================
  // EXPORT
  // ========================================

  const handleExport = useCallback(() => {
    const content =
      state.editorMode === 'visual'
        ? JSON.stringify({ recipe: state.recipe }, null, 2)
        : state.yamlContent;

    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recipe-${recipeId || 'export'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state.editorMode, state.recipe, state.yamlContent, recipeId]);

  return {
    // Editor ref (to be set by YamlEditor)
    editorRef,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Monaco editor type
    setEditorRef: (ref: any) => {
      editorRef.current = ref;
    },

    // File operations
    handleUndo,
    handleRedo,
    handleSave,
    toggleAutosave,
    handleDiscardChanges,
    handleExport,

    // Execution
    handleRun,
    executeFromEventContext,
    swapJobType,

    // Deployment
    handleDeploy,

    // Panels/Views
    toggleExecutionLog,
    toggleCompare,
    toggleMetadata,

    // Editor utilities
    toggleComment,
    formatDocument,
    toggleSchemaFold,
    toggleMinimap,
    openSqlEditor,

    // Editor mode
    handleEditorModeChange,

    // Version management
    openVersionsDialog,
    openCheckInDialog,

    // Modals
    openScheduleDialog,
    openTemplateDialog,
    openCommandPalette,

    // Menu handlers
    handleSaveMenuOpen,
    handleRunMenuOpen,
    handleToolsMenuOpen,
    handleTemplateMenuOpen,
    handleEditMenuOpen,
    handleViewMenuOpen,
    handleEnvironmentChange,

    // Menu anchor states
    saveMenuAnchor,
    runMenuAnchor,
    toolsMenuAnchor,
    templateMenuAnchor,
    editMenuAnchor,
    viewMenuAnchor,

    // Error state
    lastError,
    clearError: () => setLastError(null),
  };
};
